import {
  FC, createContext, useCallback, useContext, useMemo, useRef, useState,
} from 'react';
import { FullBoatSearchDetail } from 'src/common/types/api/v4/boats/search';
import getPaginatedInstabooks from 'src/common/utils/getPaginatedInstabooks';
import { useCurrencyContext } from 'src/common/providers/CurrencyProvider';
import { formatDate } from 'src/common/utils/dateHelpers';
import type { InstabookAvailability } from 'src/common/types/api/v4/instabook';
import getInstabookDates from 'src/trip-cart-deprecated/utils/getInstabookDates';
import { endOfMonth, startOfMonth } from 'date-fns';
import {
  DEPARTURE_DATE_FIELD,
  EMAIL,
  FIRST_NAME,
  PARAM_KEYS,
  PHONE,
  type BookNowDirectFormState,
  type FormStep,
  type SessionStorageData,
} from 'src/book-now-direct/constants';
import { filterInstabooks, hashedIdFromDirectBookingUrl, patchInstabook } from 'src/book-now-direct/helpers';
import { useUserContext } from 'src/common/providers/UserProvider';
import getAuthenticatedRedirectUrl from 'src/common/utils/getAuthenticatedRedirectUrl';
import { sessionStore } from 'src/common/utils/storageWrapper';
import { v4 as uuidv4 } from 'uuid';
import { PATHS } from 'src/common/constants';
import { useRouter } from 'next/router';
import { FORM_ERROR, type SubmissionErrors } from 'final-form';
import isApiResponseError from 'src/common/utils/isApiResponseError';
import { sortInstabookTripsByPrice } from 'src/listing/helper';
import useGrecaptcha from 'src/common/hooks/useGrecaptcha';
import captureException from 'src/common/utils/captureException';
import { useTranslation } from 'next-i18next';
import { BlockedTime } from 'src/common/providers/useBookingMotivators';
import { apiRequest } from 'src/common/utils/apiFetch';
import useFeatureFlag from 'src/common/hooks/useFeatureFlag';

type BookNowContextReturn = {
  listing: FullBoatSearchDetail;
  loading: boolean;
  fetchAndUpdateInstabooksOnDate: (
    date: Date, guestCount: number
  ) => Promise<InstabookAvailability[]>;
  allInstabooksOnDate: InstabookAvailability[];
  fetchInstabookDatesForMonth: (date: Date) => Promise<string[]>;
  /** For datepicker's `enableSpecificDates`. The first 2 months are pre-fetched on server. */
  instabookDates: string[];
  signInThenCheckout: (values: BookNowDirectFormState) => void;
  formStep: FormStep;
  changeStep: (step: FormStep) => void;
  /** Selecting calendar dates should display instabook cards without affecting filters yet  */
  previewInstabooksOnDate: InstabookAvailability[];
  /** commit preview instabooks to allInstabooksOnDate state */
  commitPreviewInstabooks: () => void;
  submitForm: (values: BookNowDirectFormState) => Promise<SubmissionErrors | void>;
  blockedTimes: BlockedTime[];
  blockedTimesLoading: boolean;
  updateBlockedTimesForDate: (listingId: string, date: Date) => Promise<void>;
};
const BookNowContext = createContext<BookNowContextReturn | null>(null);

type ProviderProps = {
  children: React.ReactNode;
  listing: FullBoatSearchDetail;
  initialInstabookDates: string[];
  initialInstabooksOnDate: InstabookAvailability[];
  blockedTimes: BlockedTime[];
};
const BookNowDirectProvider: FC<ProviderProps> = ({
  children,
  listing,
  initialInstabookDates,
  initialInstabooksOnDate,
  blockedTimes: initialBlockedTimes,
}) => {
  const isAuthMigrationEnabled = useFeatureFlag('auth-migration');
  const [t] = useTranslation('book-now-direct');
  const [allInstabooksOnDate, setAllInstabooksOnDate] = useState<InstabookAvailability[]>(
    initialInstabooksOnDate.sort(sortInstabookTripsByPrice),
  );
  const [previewInstabooksOnDate, setPreviewInstabooksOnDate] = useState<InstabookAvailability[]>(
    allInstabooksOnDate,
  );
  const [instabookDates, setInstabookDates] = useState(initialInstabookDates);
  const [formStep, setFormStep] = useState<FormStep>('details');
  const [blockedTimes, setBlockedTimes] = useState<BlockedTime[]>(initialBlockedTimes);
  const [blockedTimesLoading, setBlockedTimesLoading] = useState(false);

  const { user } = useUserContext();

  const [loading, setLoading] = useState(false);
  const { currency } = useCurrencyContext();

  const instabookCallId = useRef(0);

  const updateBlockedTimesForDate = useCallback(async (
    listingId: string,
    date: Date,
  ): Promise<void> => {
    try {
      setBlockedTimesLoading(true);
      const url = `/api/v4/boats/${listingId}/blocked-times/?date=${formatDate(date)}`;
      const response = await apiRequest(url);
      if (response.status === 200) {
        const data: BlockedTime[] = await response.json();
        setBlockedTimes(data);
      } else {
        setBlockedTimes([]);
      }
    } catch (error) {
      captureException(error);
      setBlockedTimes([]);
    } finally {
      setTimeout(() => {
        setBlockedTimesLoading(false);
      }, 300);
    }
  }, []);

  /**
   * Instabook response is paginated. If a new date is selected, then any
   * followup pagination requests for the previous (now invalid) date should be cancelled.
   */
  const fetchAndUpdateInstabooksOnDate = useCallback(async (
    date: Date,
    guestCount: number,
  ): Promise<InstabookAvailability[]> => {
    setLoading(true);
    // Increase the callId so that any older requests for this callback will be cancelled.
    instabookCallId.current += 1;
    const callId = instabookCallId.current;
    // Increase the callId so that any older requests for this callback will be cancelled.
    let instabooks: InstabookAvailability[] | null = null;
    const pagination = getPaginatedInstabooks({
      listingId: listing.id,
      startDate: formatDate(date),
      endDate: formatDate(date),
      capacity: guestCount.toString(),
      currency: currency.code,
      isDirectBooking: true,
    });
    for (;;) {
      // Cannot parallelize pagination
      // eslint-disable-next-line no-await-in-loop
      const page = await pagination.next();
      // Since `page` is awaited, there might have been another call to
      // `getInstabooks` in the meantime. Abort this process if that happens,
      //  as the other call has more recent parameters
      if (instabookCallId.current !== callId) {
        return [];
      }
      // Only the final value will contain all trips
      if (page.done) {
        instabooks = page.value;
        break;
      }
    }
    // aborted
    if (!instabooks) return [];
    setPreviewInstabooksOnDate(instabooks.sort(sortInstabookTripsByPrice));
    setLoading(false);
    return instabooks;
  }, [currency.code, listing.id]);

  const commitPreviewInstabooks = useCallback(() => {
    if (previewInstabooksOnDate) {
      setAllInstabooksOnDate(previewInstabooksOnDate);
    }
  }, [previewInstabooksOnDate]);

  const fetchInstabookDatesForMonth = useCallback(async (date: Date) => {
    setLoading(true);
    const startDate = formatDate(startOfMonth(date));
    const endDate = formatDate(endOfMonth(date));
    const newDates = await getInstabookDates(listing.id, startDate, endDate);
    // Preserve previous dates, ensure unique
    setInstabookDates((prevDates) => Array.from(new Set(prevDates.concat(newDates))));
    setLoading(false);
    return newDates;
  }, [listing]);

  const { replace } = useRouter();
  const writeSessionStorage = useCallback((
    values: BookNowDirectFormState,
    instabook: InstabookAvailability,
  ) => {
    const key = uuidv4();
    sessionStore.setItem(key, JSON.stringify({
      formData: {
        ...values,
        [DEPARTURE_DATE_FIELD]: values[DEPARTURE_DATE_FIELD]?.toISOString(),
        [PHONE]: undefined,
        [EMAIL]: undefined,
        [FIRST_NAME]: undefined,
      },
      listingId: listing.id,
      instabookId: instabook.id.toString(),
    } as SessionStorageData));
    const params = new URLSearchParams({
      [PARAM_KEYS.SESSION_KEY]: key,
    });
      // Ensure browser backs include ?session_key. Shallow to avoid SSR.
    replace(`/trips/${listing.id}${PATHS.BOOK_NOW_DIRECT}?${params.toString()}`, undefined, { shallow: true });
    return key;
  }, [listing.id, replace]);

  const signInThenCheckout = useCallback((values: BookNowDirectFormState) => {
    const instabook = filterInstabooks(allInstabooksOnDate, values)?.[0];
    if (!instabook) {
      setFormStep('details');
      return;
    }
    const sessionKey = writeSessionStorage({
      ...values,
      // if email field contains an already regsitered email, resulting in `401 PATCH`
      // Ensure email is not included in sessionStorage data to avoid the same 401 PATCH after login
      firstName: undefined,
      email: undefined,
      phone: undefined,
    }, instabook);
    const params = new URLSearchParams({
      [PARAM_KEYS.SESSION_KEY]: sessionKey,
    });
    /** On login success */
    const checkoutServer = `/trips/${listing.id}${PATHS.BOOK_NOW_DIRECT_CHECKOUT}?${params.toString()}`;
    /** When closing login modal, via 'x' icon */
    const returnUrl = `/trips/${listing.id}${PATHS.BOOK_NOW_DIRECT}?${params.toString()}`;
    window.location.href = getAuthenticatedRedirectUrl(
      checkoutServer,
      { return: returnUrl },
      isAuthMigrationEnabled,
    );
  }, [allInstabooksOnDate, listing.id, writeSessionStorage, isAuthMigrationEnabled]);

  /**  If user is authenticated, immediately PATCH the instabook and redirect to checkout.  */
  const submitStepDetails = useCallback(async (
    values: BookNowDirectFormState,
    instabook: InstabookAvailability,
  ) => {
    if (user?.id) {
      const { pk } = await patchInstabook(instabook.id, values);
      const sessionKey = writeSessionStorage(values, instabook);
      const params = new URLSearchParams({
        [PARAM_KEYS.TRIP_PK]: pk.toString(),
        [PARAM_KEYS.SESSION_KEY]: sessionKey,
      });
      window.location.href = `/trips/${listing.id}${PATHS.BOOK_NOW_DIRECT_CHECKOUT}?${params.toString()}`; // must set location when pushing to server
    } else {
      // else proceed to guest step
      setFormStep('guest');
    }
    // return undefined;
  }, [listing.id, user?.id, writeSessionStorage]);

  const {
    renewRecaptchaAndSetCookie,
    grecaptchaErrorMessage,
  } = useGrecaptcha(
    'direct_instabook_guest_checkout',
    formStep === 'guest', // only load SDK on guest step
  );

  const submitStepGuest = useCallback(async (values: {
    formState: BookNowDirectFormState;
    instabook: InstabookAvailability;
  }) => {
    // try to register guest email and push to checkout server
    const {
      pk,
      direct_booking_url: directBookingUrl,
    } = await patchInstabook(Number(values.instabook?.id), values.formState);
      // if 200, guest user was registered, proceed to checkout with tripPk
    const sessionKey = writeSessionStorage(values.formState, values.instabook);
    const params = new URLSearchParams({
      [PARAM_KEYS.TRIP_PK]: pk.toString(),
      [PARAM_KEYS.HASHED_ID]: hashedIdFromDirectBookingUrl(directBookingUrl),
      [PARAM_KEYS.SESSION_KEY]: sessionKey,
    });
    const checkoutServer = `/trips/${listing.id}${PATHS.BOOK_NOW_DIRECT_CHECKOUT}?${params.toString()}`;
    window.location.href = checkoutServer;
    return undefined;
  }, [listing.id, writeSessionStorage]);

  const submitForm = useCallback(async (
    values: BookNowDirectFormState,
  ): Promise<SubmissionErrors | void> => {
    const instabook = filterInstabooks(allInstabooksOnDate, values)?.[0];
    if (!instabook) {
      setFormStep('details'); // ask user to input new form values
      captureException('failed to narrow instabook from formValues');
      return { [FORM_ERROR]: 'error-generic' };
    }
    try {
      if (formStep === 'details') {
        await submitStepDetails(values, instabook);
      }
      if (formStep === 'guest') {
        await renewRecaptchaAndSetCookie();
        await submitStepGuest({ formState: values, instabook });
      }
    } catch (error) {
      if (error?.message === grecaptchaErrorMessage) {
        return {
          [FORM_ERROR]: 'There was an error with reCAPTCHA. Please reload the page and try again.',
        };
      }
      if (isApiResponseError(error, 401) && formStep === 'guest') {
        return {
          [EMAIL]: t('guest-email-error-label-already-exists'),
        };
      }
      if (isApiResponseError(error)) {
        const json = await error.response.json();
        if (json?.detail) {
          return { [FORM_ERROR]: json.detail }; // ask backend to translate as needed
        }
      }
      captureException(error);
      return { [FORM_ERROR]: t('error-generic') };
    }
    return undefined;
  }, [
    allInstabooksOnDate,
    formStep,
    grecaptchaErrorMessage,
    renewRecaptchaAndSetCookie,
    submitStepDetails,
    submitStepGuest,
    t,
  ]);

  const changeStep = useCallback((step: FormStep) => {
    setFormStep(step);
    return undefined;
  }, []);

  const contextValue = useMemo(() => (
    {
      listing,
      allInstabooksOnDate,
      previewInstabooksOnDate,
      loading,
      fetchAndUpdateInstabooksOnDate,
      fetchInstabookDatesForMonth,
      commitPreviewInstabooks,
      instabookDates,
      signInThenCheckout,
      formStep,
      changeStep,
      submitForm,
      blockedTimes,
      blockedTimesLoading,
      updateBlockedTimesForDate,
    }
  ), [
    listing,
    fetchAndUpdateInstabooksOnDate,
    previewInstabooksOnDate,
    fetchInstabookDatesForMonth,
    commitPreviewInstabooks,
    loading,
    allInstabooksOnDate,
    instabookDates,
    signInThenCheckout,
    formStep,
    changeStep,
    submitForm,
    blockedTimes,
    blockedTimesLoading,
    updateBlockedTimesForDate,
  ]);

  return (
    <BookNowContext.Provider value={contextValue}>
      {children}
    </BookNowContext.Provider>
  );
};

export default BookNowDirectProvider;

export const useBookNowDirectContext = (): BookNowContextReturn => {
  const context = useContext(BookNowContext);

  if (!context) {
    throw new Error('useBookNowDirectContext must be used within a BookNowDirectProvider');
  }

  return context;
};
